import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { Clock, AlertTriangle, CheckCircle, Volume2, VolumeX } from 'lucide-react';
import { MapView } from '../components/map/MapView';
import type { NarrativeMeta } from '../components/map/CameraController';
import { AlarmOverlay, isTTSEnabled, setTTSEnabled, speakNarrative } from '../components/map/NarrativeOverlay';
import { TrafficSignalDot } from '../components/map/TrafficSignalDot';
import { IntersectionMarkers } from '../components/map/IntersectionMarkers';
import { CabinetMarker } from '../components/map/CabinetMarker';
import { FloatingPanel } from '../components/map/FloatingPanel';
import { PhaseTimeline } from '../components/map/PhaseTimeline';
import { AlarmPanel, useAlarmCounts } from '../components/guadalajara/AlarmPanel';
import { useIntersectionState } from '../hooks/useIntersectionState';
import { usePlanSchedule } from '../hooks/usePlanSchedule';
import { useDetailedSimulation, AlarmCodes } from '../store/detailedSimulation';
import { PLAN_CONFIGS } from '../data/planSchedule';
import {
  guadalajaraStaticSignals,
  guadalajaraIntersections,
  GUADALAJARA_BOUNDS,
  GUADALAJARA_CENTER,
  GUADALAJARA_ZOOM,
} from '../data/guadalajara';
import { RegulatorState } from '../types/protocol';
import type { IntersectionDefinition } from '../types/intersection';
import { DemoHighlights } from '../components/guadalajara/HighlightRing';
import { useDemoTour } from '../store/demoTour';

/** Renders one simulated regulator: animated markers + cabinet */
function SimulatedRegulator({
  intersection,
  alarmCount = 0,
  plan,
  forceOpen = false,
}: {
  intersection: IntersectionDefinition;
  alarmCount?: number;
  plan: number;
  forceOpen?: boolean;
}) {
  const {
    groupColors,
    currentPhase,
    cyclePosition,
    cycleTime,
    isRunning,
  } = useIntersectionState(intersection);

  return (
    <>
      <IntersectionMarkers
        groups={intersection.groups}
        groupColors={groupColors}
      />
      <CabinetMarker
        cabinet={intersection.cabinet}
        state={RegulatorState.REMOTE_CONTROL}
        isConnected={isRunning}
        currentPlan={plan}
        currentPhase={currentPhase}
        cyclePosition={cyclePosition}
        cycleTime={cycleTime}
        groupCount={intersection.groups.length}
        detectorCount={0}
        alarmCount={alarmCount}
        forceOpen={forceOpen}
      />
    </>
  );
}

/** Cycle panel — only rendered when near a regulator */
function CyclePanel({
  intersection,
  phases,
  plan,
  planLabel,
  alarmCount,
}: {
  intersection: IntersectionDefinition;
  phases: { id: string; duration: number }[];
  plan: number;
  planLabel: string;
  alarmCount: number;
}) {
  const { currentPhase, cyclePosition, cycleTime } = useIntersectionState(intersection);
  const id = intersection.cabinet.logicalId;
  const nVeh = intersection.groups.filter(g => g.type === 'V').length;
  const nPed = intersection.groups.filter(g => g.type === 'P').length;

  return (
    <FloatingPanel
      title={intersection.name}
      position="bottom-center"
      highlightId="cycle-panel"
      icon={<span className={`text-xs font-mono font-bold ${REGULATOR_COLORS[id]}`}>R{id}</span>}
    >
      <div className="flex items-stretch gap-0 min-w-[420px] sm:min-w-[520px]">
        {/* Left: regulator info */}
        <div className="flex flex-col justify-center gap-1 px-3 py-2 border-r border-gray-700/30">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-500">Plan</span>
            <span className="text-[11px] font-mono font-medium text-cyan-400">P{plan}</span>
            <span className="text-[10px] text-gray-500">{planLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500">Grupos</span>
            <span className="text-[11px] font-mono text-gray-300">{nVeh}V + {nPed}P</span>
          </div>
        </div>
        {/* Center: phase timeline */}
        <div className="flex-1">
          <PhaseTimeline
            phases={phases}
            currentPhase={currentPhase}
            cyclePosition={cyclePosition}
            cycleTime={cycleTime}
          />
        </div>
        {/* Right: alarm badge */}
        <div className="flex items-center gap-1 px-3 border-l border-gray-700/30">
          {alarmCount > 0 ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
              <span className="text-[11px] font-mono font-medium text-red-400">{alarmCount}</span>
            </>
          ) : (
            <CheckCircle className="w-3.5 h-3.5 text-green-400" />
          )}
        </div>
      </div>
    </FloatingPanel>
  );
}

const REGULATOR_COLORS: Record<number, string> = {
  1: 'text-cyan-400',
  2: 'text-amber-400',
  3: 'text-emerald-400',
  4: 'text-violet-400',
};

const REGULATOR_DOT_COLORS: Record<number, string> = {
  1: 'bg-cyan-400',
  2: 'bg-amber-400',
  3: 'bg-emerald-400',
  4: 'bg-violet-400',
};

/** Single regulator status chip for the overview bar */
function RegulatorChip({ intersection, alarmCount }: { intersection: IntersectionDefinition; alarmCount: number }) {
  const { currentPhase } = useIntersectionState(intersection);
  const id = intersection.cabinet.logicalId;
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-1.5 h-1.5 rounded-full ${alarmCount > 0 ? 'bg-red-500 animate-pulse' : REGULATOR_DOT_COLORS[id]}`} />
      <span className={`text-[11px] font-mono font-bold ${REGULATOR_COLORS[id]}`}>R{id}</span>
      <span className="text-[11px] font-mono text-white">{currentPhase}</span>
    </div>
  );
}

/** Overview bar — all 4 regulators at a glance, shown when zoomed out */
function OverviewBar({ alarmCounts }: { alarmCounts: Record<number, number> }) {
  return (
    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10" data-highlight="overview-bar">
      <div className="bg-gray-900/90 backdrop-blur-md border border-gray-700/50 rounded-lg shadow-2xl px-4 py-2 flex items-center gap-4">
        {guadalajaraIntersections.map(ix => (
          <RegulatorChip key={ix.id} intersection={ix} alarmCount={alarmCounts[ix.cabinet.logicalId] || 0} />
        ))}
      </div>
    </div>
  );
}

/** Find nearest intersection to map center (returns null if too far or zoomed out) */
function findNearbyIntersection(
  center: [number, number],
  zoom: number,
): IntersectionDefinition | null {
  if (zoom < 15.5) return null;
  let best: IntersectionDefinition | null = null;
  let bestDist = Infinity;
  for (const ix of guadalajaraIntersections) {
    const dx = center[0] - ix.center[0];
    const dy = center[1] - ix.center[1];
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      best = ix;
    }
  }
  // ~0.004° ≈ 400m threshold
  if (bestDist > 0.004 * 0.004) return null;
  return best;
}

interface GuadalajaraMapViewProps {
  alarms: import('../components/guadalajara/AlarmPanel').MockAlarm[];
  addAlarm: (driverId: number, code: string) => void;
  resolveAlarm: (id: number) => void;
  cleanResolved: () => void;
}

export function GuadalajaraMapView({ alarms, addAlarm, resolveAlarm, cleanResolved }: GuadalajaraMapViewProps) {
  // Plan schedule — drives plan changes based on real clock
  const { clock, activePlan } = usePlanSchedule();
  const phaseDurations = useDetailedSimulation(s => s.phaseDurations);
  const currentPlan = useDetailedSimulation(s => s.currentPlan);
  const cycleTime = useDetailedSimulation(s => s.cycleTime);

  // Dynamic phases from store (updated when plan changes)
  const phases = useMemo(() => [
    { id: 'F1', duration: phaseDurations[0] },
    { id: 'F2', duration: phaseDurations[1] },
    { id: 'F3', duration: phaseDurations[2] },
    { id: 'F4', duration: phaseDurations[3] },
  ], [phaseDurations]);

  // Track which regulator is near the map center
  const [nearbyIntersection, setNearbyIntersection] = useState<IntersectionDefinition | null>(null);
  const [cameraDebug, setCameraDebug] = useState<{ center: [number, number]; zoom: number; bearing: number; pitch: number } | null>(null);
  const [showDebug, setShowDebug] = useState(false);

  const handleViewState = useCallback((center: [number, number], zoom: number, bearing: number, pitch: number) => {
    setNearbyIntersection(findNearbyIntersection(center, zoom));
    setCameraDebug({ center, zoom, bearing, pitch });
  }, []);

  // Toggle camera debug overlay with 'd' key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'd' && !e.ctrlKey && !e.metaKey && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        setShowDebug(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const alarmCounts = useAlarmCounts(alarms);
  const demoHighlights = useDemoTour(s => s.highlights);
  const cabinetPanelsOpen = demoHighlights.includes('cabinet-panels');

  const [alarmMeta, setAlarmMeta] = useState<NarrativeMeta | null>(null);
  const [ttsOn, setTtsOn] = useState(isTTSEnabled);

  // Alarm loop post-tour: random alarms every 20-35s, fly camera to regulator
  const alarmLoopActive = useDemoTour(s => s.alarmLoopActive);
  const alarmLoopRef = useRef<ReturnType<typeof setInterval>>();
  const alarmsRef = useRef(alarms);
  alarmsRef.current = alarms;
  useEffect(() => {
    if (!alarmLoopActive) {
      clearInterval(alarmLoopRef.current);
      return;
    }
    const ALARM_CODES = ['A', 'B', 'C', 'E', 'I', 'J', 'K', 'L', 'N', 'V'];
    const REG_NAMES: Record<number, string> = { 1: 'Plaza Mayor', 2: 'Av. Ejército', 3: 'Glorieta Boixareu', 4: 'C/ del Trabajo' };
    const CAMERA_URL = import.meta.env.DEV
      ? 'http://localhost:8310/camera/command'
      : '/camera/command';
    function fireAlarm() {
      const driverId = Math.floor(Math.random() * 4) + 1;
      const code = ALARM_CODES[Math.floor(Math.random() * ALARM_CODES.length)];
      const ix = guadalajaraIntersections.find(i => i.cabinet.logicalId === driverId);
      const desc = AlarmCodes[code as keyof typeof AlarmCodes] || code;
      const narrative = `R${driverId} ${REG_NAMES[driverId]} — !${desc}!`;
      addAlarm(driverId, code);
      // Fly camera to the affected regulator + broadcast narrative + alarm
      if (ix) {
        fetch(CAMERA_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'fly_to',
            center: ix.center,
            zoom: 18.5,
            pitch: 50,
            bearing: 0,
            duration: 2500,
            narrative,
            alarm: { driverId, code },
          }),
        }).catch(() => {});
      }
      // ~30% double alarm (no camera move, just inject)
      if (Math.random() < 0.3) {
        setTimeout(() => {
          const d2 = Math.floor(Math.random() * 4) + 1;
          const c2 = ALARM_CODES[Math.floor(Math.random() * ALARM_CODES.length)];
          addAlarm(d2, c2);
        }, 3000 + Math.random() * 2000);
      }

      // Resolve oldest active alarm (>30s old) — delayed so narrative doesn't clash
      const now = Date.now();
      const oldest = alarmsRef.current
        .filter(a => !a.resolvedAt && now - a.timestamp > 30000)
        .sort((a, b) => a.timestamp - b.timestamp)[0];
      if (oldest) {
        const resolveId = oldest.id;
        const resolvedDesc = AlarmCodes[oldest.code as keyof typeof AlarmCodes] || oldest.code;
        const resolveNarrative = `R${oldest.driverId} ${REG_NAMES[oldest.driverId]} — *${resolvedDesc} resuelta*`;
        setTimeout(() => {
          resolveAlarm(resolveId);
          fetch(CAMERA_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'fly_to', narrative: resolveNarrative, duration: 0 }),
          }).catch(() => {});
        }, 8000);
      }

      // Clean resolved alarms older than 30s
      cleanResolved();
    }
    const scheduleNext = () => 20000 + Math.random() * 15000;
    alarmLoopRef.current = setInterval(fireAlarm, scheduleNext());
    return () => clearInterval(alarmLoopRef.current);
  }, [alarmLoopActive, addAlarm, resolveAlarm, cleanResolved]);

  // Note: alarm data flows via WS → AlarmOverlay (toasts) only.
  // addAlarm() is called directly where needed — no WS→addAlarm path (avoids duplication).

  // Format next change time
  const nextChangeLabel = activePlan.nextChange
    ? `${String(activePlan.nextChange.hour).padStart(2, '0')}:${String(activePlan.nextChange.minute).padStart(2, '0')} → P${activePlan.nextChange.plan}`
    : null;

  return (
    <div className="relative w-full h-full">
      <div className="absolute inset-0 map-container">
        <MapView
          center={GUADALAJARA_CENTER}
          zoom={GUADALAJARA_ZOOM}
          cameraControl
          onCameraAlarm={undefined}
          onAlarmMeta={setAlarmMeta}
          onViewState={handleViewState}
          bearing={0}
          pitch={0}
          maxBounds={GUADALAJARA_BOUNDS}
          minZoom={13}
          maxZoom={19}
        >
          {guadalajaraStaticSignals.map(signal => (
            <TrafficSignalDot key={signal.id} signal={signal} />
          ))}

          {guadalajaraIntersections.map(intersection => (
            <SimulatedRegulator
              key={intersection.id}
              intersection={intersection}
              alarmCount={alarmCounts[intersection.cabinet.logicalId] || 0}
              plan={currentPlan}
              forceOpen={cabinetPanelsOpen}
            />
          ))}
        </MapView>
      </div>

      {/* Alarm overlay — camera-injected, independent from other panels */}
      <AlarmOverlay meta={alarmMeta} />

      {/* Alarm panel */}
      <AlarmPanel alarms={alarms} highlightId="alarm-panel" />

      {/* Bottom center: cycle detail when near a regulator, overview when zoomed out */}
      {nearbyIntersection ? (
        <CyclePanel
          intersection={nearbyIntersection}
          phases={phases}
          plan={currentPlan}
          planLabel={PLAN_CONFIGS[currentPlan]?.label || ''}
          alarmCount={alarmCounts[nearbyIntersection.cabinet.logicalId] || 0}
        />
      ) : (
        <OverviewBar alarmCounts={alarmCounts} />
      )}

      {/* TTS toggle */}
      <button
        data-highlight="tts-button"
        onClick={() => { setTTSEnabled(!ttsOn); setTtsOn(!ttsOn); }}
        className={`absolute top-3 right-[170px] sm:right-[220px] z-40 p-2 rounded-lg border backdrop-blur-sm transition-colors ${ttsOn ? 'bg-cyan-900/50 border-cyan-600/50 text-cyan-400' : 'bg-gray-900/90 border-gray-700 text-gray-500 hover:text-gray-300'}`}
        title={ttsOn ? 'Desactivar narración por voz' : 'Activar narración por voz'}
      >
        {ttsOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>

      {/* Clock + Plan badge */}
      <div data-highlight="clock-badge" className="absolute top-3 right-3 bg-gray-900/90 backdrop-blur-sm rounded-lg border border-gray-700 px-2 py-1.5 sm:px-3 sm:py-2 z-40">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-400" />
            <span className="text-xs sm:text-sm font-mono font-bold text-white">{clock}</span>
          </div>
          <span className="text-[10px] text-gray-500 hidden sm:inline">Guadalajara</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 sm:mt-1">
          <span className="text-[11px] sm:text-xs font-mono font-medium text-cyan-400">
            P{currentPlan}
          </span>
          <span className="text-[10px] text-gray-500 hidden sm:inline">
            {PLAN_CONFIGS[currentPlan]?.label}
          </span>
          <span className="text-[10px] text-gray-400">
            {(cycleTime / 10).toFixed(0)}s
          </span>
        </div>
        {nextChangeLabel && (
          <div className="text-[10px] text-gray-500 mt-0.5 hidden sm:block">
            próx: {nextChangeLabel}
          </div>
        )}
      </div>

      {/* Camera debug overlay — toggle with 'd' key */}
      {showDebug && cameraDebug && (
        <div className="absolute top-14 left-3 z-50 bg-black/80 backdrop-blur-sm rounded-lg border border-gray-600 px-3 py-2 font-mono text-[11px] text-green-400 select-all">
          <div className="text-gray-500 text-[9px] mb-1">Camera (press d to hide)</div>
          <div>center: [{cameraDebug.center[0].toFixed(4)}, {cameraDebug.center[1].toFixed(4)}]</div>
          <div>zoom: {cameraDebug.zoom.toFixed(1)}</div>
          <div>bearing: {cameraDebug.bearing.toFixed(1)}</div>
          <div>pitch: {cameraDebug.pitch.toFixed(1)}</div>
        </div>
      )}

      {/* Demo highlight overlays — zero layout impact */}
      <DemoHighlights />
    </div>
  );
}
